"use client";

import { useState } from "react";

import { CardPhoto } from "@/components/ui/card-photo";
import FilterBar from "@/components/FilterBar";
import ExperienceModal from "@/components/ExperienceModal";

import { ExperienceItem } from "@/data/experience";
import type { FilterGroup } from "@/data/filters";
import { defaultSortConfigs, sortItems } from "@/lib/sortUtils";
import { track } from "@/utils/tracking";

type ExperienceClientProps = {
  initialExperience: ExperienceItem[];
  experienceFilters: FilterGroup[];
  locale: string;
};

export default function ExperienceClient({ 
  initialExperience, 
  experienceFilters
}: ExperienceClientProps) {
  
  // Initialize state for selected item and modal open state
  const [selectedItem, setSelectedItem] = useState<ExperienceItem | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Initialize filtered items state
  const [filteredExperience, setFilteredExperience] = useState<ExperienceItem[]>(initialExperience);
  
  // Handle filter changes
  const handleFilterChange = (activeFilters: Record<string, string | null>) => {
    const hasActiveFilters = Object.values(activeFilters).some(filter => filter !== null);
    
    if (!hasActiveFilters) {
      // No filters active, show initial experience
      setFilteredExperience(initialExperience);
    } else {
      // Filters active, show filtered content
      // Import experience data dynamically for filtering
      import('@/data/experience').then(({ default: experience }) => {
        const filtered = experience.filter(item => {
          return Object.values(activeFilters).every(activeFilter => {
            if (activeFilter === null) return true;
            return item.tags.includes(activeFilter);
          });
        });
        
        setFilteredExperience(sortItems(filtered, defaultSortConfigs.experience));
      });
    }
  };

  // Handle card click
  const handleCardClick = (item: ExperienceItem) => {
    track('experience_item_clicked', {
      title: item.name
    });
    setSelectedItem(item);
    setModalOpen(true);
  };

  return (
    <>
      {/* Only show filter bar if there are filter groups configured */}
      {experienceFilters.length > 0 && (
        <FilterBar 
          filterGroups={experienceFilters}
          onFilterChange={handleFilterChange}
          className="mb-6"
        />
      )}
      
      {/* Always render cards client-side with proper event handlers */}
      <ul className="grid-template-logos mt-6 mb-2">
        {filteredExperience.map((item, index) => (
          <li key={`${item.name}-${index}`}>
            <CardPhoto
              className="cursor-pointer"
              photo={item.image}
              photoDark={item.imageDark}
              alt={item.name}
              onClick={() => handleCardClick(item)}
              tags={item.tags}
            />
          </li>
        ))}
      </ul>

      {/* Modal */}
      {selectedItem && (
        <ExperienceModal 
          item={selectedItem} 
          itemIndex={filteredExperience.indexOf(selectedItem)}
          open={modalOpen} 
          onOpenChange={setModalOpen}
        />
      )}
    </>
  );
}